import { Injectable, inject } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { switchMap, catchError, of, map, finalize, exhaustMap, tap, withLatestFrom, Observable, filter } from 'rxjs'
import * as TutorialVideosActions from './tutorial-videos.actions'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { TutorialVideosApiService } from './tutorial-videos-api.service'
import { GbAddEditTutorialVideoComponent } from 'libs/gb-dashboard/staff/feature-shell/src/lib/gb-add-edit-tutorial-video/gb-add-edit-tutorial-video.component'
import { TUTORIAL_VIDEOS_LOADER } from 'gb-dashboard/staff/data-access-tutorial-videos'
import { GbTutorialPopupVideoComponent } from 'libs/gb-dashboard/staff/feature-shell/src/lib/gb-tutorial-popup-video/gb-tutorial-popup-video.component'
import { GbSidenavVisibilityService } from 'libs/gb-dashboard/core/feature/src/lib/gb-dashboard-sidenav/gb-sidenav-visibility.service'
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout'

@Injectable()
export class TutorialVideosEffects {
  private actions$ = inject(Actions)

  loadTutorialVideos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TutorialVideosActions.loadTutorialVideos),
      switchMap(() =>
        this.tutorialVideosApiService.getTutorialVideos().pipe(
          map((res) => TutorialVideosActions.loadTutorialVideosSuccess({ tutorialVideos: res })),
          catchError((error) => of(TutorialVideosActions.loadTutorialVideosFailure({ error }))),
        ),
      ),
    ),
  )

  openEditTutorialVideoDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TutorialVideosActions.openEditTutorialVideoDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbAddEditTutorialVideoComponent, {
            maxWidth: '600px',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  closeEditTutorialVideoDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TutorialVideosActions.addTutorialVideoSuccess, TutorialVideosActions.updateTutorialVideoSuccess),
        tap(() => this.gbDashboardDialogService.closeDialog(GbAddEditTutorialVideoComponent)),
      ),
    { dispatch: false },
  )

  popUpVideoRange$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.Small, Breakpoints.Web])
    .pipe(map((breakpoint) => breakpoint.matches))

  showTutorialPopUpVideo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TutorialVideosActions.showTutorialPopUpVideo),
        withLatestFrom(this.gbSidenavVisibilityService.sidenavVisible$, this.popUpVideoRange$),
        filter(([, , isInRange]) => isInRange),
        switchMap(([action, isVisible, isTablet]) =>
          this.gbDashboardDialogService.getDialogObs(GbTutorialPopupVideoComponent, {
            disableClose: true,
            hasBackdrop: false,
            maxWidth: 650,
            position: { bottom: '10px', left: isVisible ? '366px' : '96px' },
            data: {
              videos: action.videos.map((video) => ({ videoId: video.video_url, title: video.title })),
              target: action.target,
            },
            exitAnimationDuration: 400,
            panelClass: 'gb-add-edit-tutorial-video-dialog',
          }),
        ),
      ),
    { dispatch: false },
  )

  closeTutorialPopUpVideoDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TutorialVideosActions.hideTutorialPopUpVideo),
        tap(() => this.gbDashboardDialogService.closeDialog(GbTutorialPopupVideoComponent)),
      ),
    { dispatch: false },
  )

  addTutorialVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TutorialVideosActions.addTutorialVideo),
      switchMap((action) =>
        this.tutorialVideosApiService.createTutorialVideo(action.tutorialVideo).pipe(
          map((res) => TutorialVideosActions.addTutorialVideoSuccess({ tutorialVideo: res })),
          catchError((error) => of(TutorialVideosActions.addTutorialVideoFailure({ error }))),
        ),
      ),
    ),
  )

  updateTutorialVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TutorialVideosActions.updateTutorialVideo),
      switchMap((action) =>
        this.tutorialVideosApiService.updateTutorialVideo(action.tutorialVideo).pipe(
          map((res) => TutorialVideosActions.updateTutorialVideoSuccess({ tutorialVideo: res })),
          catchError((error) => of(TutorialVideosActions.updateTutorialVideoFailure({ error }))),
        ),
      ),
    ),
  )

  deleteTutorialVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TutorialVideosActions.deleteTutorialVideo),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'STAFF.TUTORIAL_VIDEOS.DELETE_CONFIRM_TITLE',
            body: 'STAFF.TUTORIAL_VIDEOS.DELETE_CONFIRM_DESC',
            confirmButtonText: 'DELETE',
            cancelButtonText: 'CANCEL',
          },
          action,
        ),
      ),
      tap(() => this.gbLoaderService.showLoaderDialog(TUTORIAL_VIDEOS_LOADER.deleteTutorialVideo.id)),
      switchMap((action) =>
        this.tutorialVideosApiService.deleteTutorialVideo(action.uuid).pipe(
          map(() => TutorialVideosActions.deleteTutorialVideoSuccess({ uuid: action.uuid })),
          catchError((error) => of(TutorialVideosActions.deleteTutorialVideoFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(TUTORIAL_VIDEOS_LOADER.deleteTutorialVideo.id)),
        ),
      ),
    ),
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TutorialVideosActions.loadTutorialVideosFailure,
          TutorialVideosActions.addTutorialVideoFailure,
          TutorialVideosActions.updateTutorialVideoFailure,
          TutorialVideosActions.deleteTutorialVideoFailure,
        ),
        tap((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private tutorialVideosApiService: TutorialVideosApiService,
    private gbLoaderService: GbLoaderService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbNotificationService: GbNotificationService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbSidenavVisibilityService: GbSidenavVisibilityService,
    private breakpointObserver: BreakpointObserver,
  ) {}
}
